const REDUX_STATE_FILENAME = 'redux_state.json'

const summarizeAction = action => {
	if (!action || typeof action !== 'object') return action

	switch (action.type) {
		case 'persist/REHYDRATE':
			return {type: action.type, key: action.key}
		case 'persist/PERSIST':
		case '@@redux/INIT':
			return {type: action.type}
		case 'MULTI_INDEX':
		case 'INDEX_RECORDS': {
			const {items = [], ...rest} = action
			return {...rest, item_count: items.length}
		}
		default:
			return action
	}
}

const summarizeRecords = records =>
	Object.entries(records || {}).reduce((summary, [kind, items]) => {
		if (Array.isArray(items) && items.length > 0) summary[kind] = items.length
		return summary
	}, {})

const summarizeStateForContext = state => {
	if (!state || typeof state !== 'object') return null

	// Keep the visible Application State context small so the event body stays under Sentry's size limit.
	return {
		reducers: Object.keys(state),
		record_counts: summarizeRecords(state.records),
		active_environment_id: state.environment?.active,
		auth: {
			type: state.auth?.type,
			user_id: state.auth?.user?.id,
		},
		persist: {
			rehydrated: Boolean(state._persist?.rehydrated),
		},
	}
}

const shouldSkipEvent = event => !event || event.type !== undefined

export const createSentryReduxEnhancer = ({sdk} = {}) => {
	if (typeof sdk?.createReduxEnhancer !== 'function' || typeof sdk?.getGlobalScope !== 'function') return undefined

	let latestState

	sdk.getGlobalScope().addEventProcessor((event, hint) => {
		if (shouldSkipEvent(event) || !latestState) return event

		hint.attachments = [
			...(hint?.attachments || []),
			{
				// Keep the full Redux store available for debugging without putting it in the normalized event body.
				filename: REDUX_STATE_FILENAME,
				contentType: 'application/json',
				data: JSON.stringify(latestState),
			},
		]

		return event
	})

	return sdk.createReduxEnhancer({
		// Disable the built-in attachment so the stateTransformer only affects the visible context, not the file attachment.
		attachReduxState: false,
		actionTransformer: summarizeAction,
		stateTransformer: summarizeStateForContext,
		configureScopeWithState: (_scope, state) => {
			// Remember the latest full state for the manual attachment added in the event processor above.
			latestState = state
		},
	})
}
